import { useMemo } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { ErrorOutline, FileDownloadOutlined } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "contexts/auth";
import api from "services/api";
import { buildCarteirinhasCsv } from "./carteirinhas-csv";
import { CARTEIRINHA_COLUMNS } from "./carteirinhas-columns";
import { ICarteirinha } from "./types";

export function CarteirinhasTab({ organizationId }: { organizationId?: string } = {}) {
  const auth = useAuth();
  const orgId = organizationId ?? auth.user?.organization?.id;

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["collaboratorCarteirinhas", orgId],
    queryFn: () =>
      api.get<{ data: ICarteirinha[] }>(`/v2/cdp/collaborators/${orgId}/carteirinhas`),
    select: (data) => data.data.data,
    enabled: !!orgId,
  });

  const columns: GridColDef<ICarteirinha>[] = useMemo(
    () =>
      CARTEIRINHA_COLUMNS.map((column) => ({
        field: column.field,
        headerName: column.header,
        width: column.width,
        renderCell: (params) => {
          const value = column.format(params.row as ICarteirinha);
          if (!column.isStatus) return value;
          if (value === "-") return "-";
          return <Chip label={value} size="small" color={/ativo/i.test(value) ? "success" : "default"} />;
        },
      })),
    [],
  );

  const rows: GridRowsProp = useMemo(() => {
    if (!data) return [];
    return data.map((item, index) => ({
      id: `${item.cpf}_${item.contractCode ?? ""}_${index}`,
      ...item,
    }));
  }, [data]);

  const handleExport = () => {
    if (!data?.length) return;

    const csv = buildCarteirinhasCsv(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // toISOString devolve "YYYY-MM-DDTHH:mm:ss.sssZ"; queremos so a data.
    link.setAttribute("href", url);
    link.setAttribute("download", `carteirinhas_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isFetched && isFetching) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={3}
      >
        <CircularProgress size={30} thickness={4} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={3}
        textAlign="center"
      >
        <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
        <Typography variant="h6" color="textPrimary">
          Ops! Algo deu errado
        </Typography>
        <Typography variant="body1" color="textSecondary" maxWidth={400}>
          Não foi possível carregar as carteirinhas no momento. Por favor, tente novamente.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: 600,
        }}
      >
        <DataGrid
          rows={rows}
          // MUI DataGrid's `columns` prop is invariant on the row model, so a typed
          // GridColDef<ICarteirinha>[] isn't assignable directly. Scoped cast (better
          // than a blanket @ts-ignore — it only relaxes the column row-type).
          columns={columns as unknown as GridColDef[]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={{
            ...ptBR.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: "Nenhuma carteirinha encontrada",
          }}
          sx={{
            border: 0,
            backgroundColor: "white",
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              borderBottom: "2px solid rgba(224, 224, 224, 1)",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: "#1E1B39",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(51, 0, 255, 0.04)",
            },
          }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlined />}
          onClick={handleExport}
          disabled={!data?.length}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Exportar
        </Button>
      </Box>
    </>
  );
}
